import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { AgremoCreditBlock } from '../data-models/agremo-credit-block.model';
import { AgremoCreditBlockCreateRequest } from '../request-models/agremo-credit-block-create-request.model';
import { AgremoCreditBlockUpdateRequest } from '../request-models/agremo-credit-block-update-request.model';
import { AgremoCreditSummary } from '../response-models/agremo-credit-summary.model';
import { toAgremoCreditCreateRequest, toAgremoCreditUpdateRequest } from '../utils/agremo-credit-request-utils';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';

/** Agremo credit blocks: `/admin/agremo/credits` (hub `admin-agremo-credits-controller.ts`). Not paged. */
@Injectable({ providedIn: 'root' })
export class AgremoCreditService extends BaseService<AgremoCreditBlock> {
  constructor() {
    super('admin/agremo/credits', AgremoCreditBlock);
  }

  /** GET /admin/agremo/credits: every block plus availability totals (`data` is an object, not a list). */
  listCredits(): Observable<AgremoCreditSummary> {
    return this.request('GET', {}, data => this.toSummary(data));
  }

  /** POST /admin/agremo/credits */
  createCredit(fields: AgremoCreditBlockCreateRequest): Observable<AgremoCreditBlock | null> {
    return this.create(toAgremoCreditCreateRequest(fields));
  }

  /** PUT /admin/agremo/credits/:id: extends the expiry and/or changes the total. */
  updateCredit(creditId: string, fields: AgremoCreditBlockUpdateRequest): Observable<AgremoCreditBlock | null> {
    return this.update(creditId, toAgremoCreditUpdateRequest(fields));
  }

  /** DELETE /admin/agremo/credits/:id: archives the block (sets `archivedAt`). */
  archiveCredit(creditId: string): Observable<void> {
    return this.delete(creditId);
  }

  /**
   * Each unit total falls back independently: a total the hub omitted stays `undefined`, so the page can
   * compute it from the blocks. `availableAnalyses` falls back to the legacy `totalAvailable` only when the
   * hub actually sent it (pre-migration, every block is analysis-typed).
   */
  private toSummary(data: unknown): AgremoCreditSummary {
    const source = isRecord(data) ? data : {};
    const rawBlocks = Array.isArray(source['blocks']) ? source['blocks'] : [];
    const totalAvailable = optionalNumber(source['totalAvailable']);
    return {
      credits: rawBlocks.map(block => this.createType(block)),
      totalAvailable: totalAvailable ?? 0,
      availableAcres: optionalNumber(source['availableAcres']),
      availableHectares: optionalNumber(source['availableHectares']),
      availableAnalyses: optionalNumber(source['availableAnalyses']) ?? totalAvailable,
      expiringBlockCount: countOrZero(source['expiringBlockCount']),
    };
  }
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
